/**
 * AtlasCaveModule — EXPLORABLE CAVES (terrain wave 2026-09-08).
 *
 * Sites come from shared/caves.ts (pure, mirrored); the chunk assembler cuts
 * the mouth out of the terrain mesh + collider; this module owns everything
 * INSIDE: the marching-cubes cavity from cave_worker (a Perlin worm heading
 * uphill into the hill, two chambers), its trimesh collider, a rock material
 * (triplanar highland texture), glowing crystals along the walls, one shared
 * point light that follows the player to the nearest chamber, and a boulder
 * collar around the mouth that hides the coarse edge of the terrain hole.
 *
 * Grounding law: while the player is INSIDE a cavity (`atlas.caveId`), the
 * physics terrain clamp yields exactly as it does for a building interior —
 * the cave's trimesh is the floor authority, the analytic ground above is not.
 *
 * Levers: window.__caves() → sites near, built interiors, the player's cave.
 * localStorage 'atlas:caves' = 'off' disables the layer (mouths stay cut).
 */
import * as THREE from 'three/webgpu';
import { texture as tslTexture, positionWorld, normalWorld, vec2, vec3, float, mix, abs, normalize, pow } from 'three/tsl';
import type { App, IModule } from 'enginex';
import { makeSystem, Phase } from '../engine-compat.js';
import { atlas } from '../state.js';
import { postRiverTable } from '../lib/hydrology_load.js';
import { disposeLater } from '../lib/gpu_dispose.js';
import { caveSites, type CaveSite } from '@shared/caves.js';
import { terrainHeight } from '@shared/terrain.js';
import { caveDensity, portalDome, CAVE_PORTAL, MOUTH_R, type CavePlan, type CaveCapsule } from '../lib/cave_gen.js';
import type { CaveJob, CaveResult } from '../workers/cave_worker.js';
import type { AssetModule } from './AssetModule.js';
import type { PhysicsModule } from './PhysicsModule.js';
import { BIOME_GROUND } from '../config.js';
import { pinStaticMotionVectors } from 'enginex/render/batching/staticMotionVectors.js';
import { RAIN_SHELTER_LAYER } from '../lib/rain_shelter.js';

const TICK_EVERY = 10;
/** Build interiors within this range of the player; drop beyond DROP_R. */
const BUILD_R = 900;
const DROP_R = 1300;
const CRYSTAL_EVERY = 2;
/** The entrance light comes on within this range of a built mouth (m). */
const MOUTH_LIGHT_M = 18;

interface Built {
  group: THREE.Group;
  colliderId: string;
  caps: CaveCapsule[];
  chambers: Array<{ x: number; y: number; z: number; r: number }>;
  site: CaveSite;
}

export class AtlasCaveModule implements IModule {
  readonly name = 'AtlasCaveModule';
  private scene!: THREE.Scene;
  private seed = '';
  private worker: Worker | null = null;
  private pending = new Set<number>();
  private built = new Map<number, Built>();
  private rockMat: THREE.MeshStandardNodeMaterial | null = null;
  private crystalMat: THREE.MeshStandardNodeMaterial | null = null;
  private light: THREE.PointLight | null = null;
  private frame = 0;
  private stats = { requested: 0, builtMs: 0, lastTriangles: 0 };

  constructor(private readonly assets: AssetModule, private readonly physics: PhysicsModule) {}

  init(app: App): void {
    this.scene = app.world.scene as unknown as THREE.Scene;
    this.rockMat = new THREE.MeshStandardNodeMaterial({ color: 0x5a5148, roughness: 0.92, metalness: 0.02, side: THREE.DoubleSide });
    this.crystalMat = new THREE.MeshStandardNodeMaterial({ color: 0x8fd8ff, emissive: 0x4fb8ff, emissiveIntensity: 2.2, roughness: 0.25, metalness: 0.1 });
    this.light = new THREE.PointLight(0x7fc8ff, 0, 34, 1.6);
    this.light.visible = false;
    this.scene.add(this.light);
    void this.assets.loadTexture(BIOME_GROUND.rocky_highlands).then((tex) => {
      if (!this.rockMat) return;
      tex.wrapS = tex.wrapT = THREE.RepeatWrapping;
      // Triplanar rock: the cavity has no UVs; project the highland texture along
      // the dominant normal axis (blend weights = |n|^4, normalised).
      const wp = positionWorld;
      const n = abs(normalize(normalWorld));
      const w = pow(n, vec3(4, 4, 4));
      const wn = w.div(w.x.add(w.y).add(w.z));
      const k = float(1 / 6);
      const tx = tslTexture(tex, vec2(wp.z, wp.y).mul(k)).mul(wn.x);
      const ty = tslTexture(tex, vec2(wp.x, wp.z).mul(k)).mul(wn.y);
      const tz = tslTexture(tex, vec2(wp.x, wp.y).mul(k)).mul(wn.z);
      const albedo = tx.add(ty).add(tz);
      // Darker, cooler inside: caves read damp; a little vertical gradient by depth.
      this.rockMat.colorNode = mix(albedo.rgb.mul(vec3(0.55, 0.56, 0.6)), albedo.rgb.mul(0.8), float(0.35));
      this.rockMat.needsUpdate = true;
    }).catch(() => { /* flat rock colour stays */ });
    this.physics.setCaveTest((x, y, z) => this.cavityAt(x, y, z));
    app.world.addSystem(makeSystem('AtlasCaves', Phase.RenderSub, 42, () => this.tick()));
    (globalThis as unknown as { __caveSurface?: (x: number, z: number) => number }).__caveSurface = (x, z) => this.seed ? terrainHeight(this.seed, x, z) : 0;
    // Probe lever: stand the avatar on the cavity floor at walk point k of a built cave.
    (globalThis as unknown as { __caveEnter?: (id: number, k: number) => unknown }).__caveEnter = (id, k) => {
      const b = this.built.get(id);
      if (!b) return null;
      const pts = capsPath(b);
      const p = pts[Math.max(0, Math.min(pts.length - 1, k))];
      if (!p) return null;
      const feet = p.y - 1.25 + 0.35;
      this.physics.placeFeet(p.x, feet, p.z);
      return { x: p.x, z: p.z, feet, floor: p.y - 1.25, surface: this.seed ? terrainHeight(this.seed, p.x, p.z) : null };
    };
    // Probe lever: the PORTAL ROOF census of a built cave — rays cast straight
    // down over the outcrop's footprint against the cave mesh AND the terrain
    // chunks (the meshes carrying `biomeWA`): every ray must land on
    // something (a miss = a hole into the void), the mouth zone must land on
    // the CAVE mesh (its outcrop roofs the ground's jagged hole), and a ray
    // along the walk axis from the porch must run ≥ 6 m before it meets rock
    // (the arch is open).
    (globalThis as unknown as { __cavePortal?: (id: number) => unknown }).__cavePortal = (id) => {
      const b = this.built.get(id);
      if (!b) return null;
      const s = b.site;
      const caveMeshes: THREE.Object3D[] = [];
      b.group.traverse((o) => { if ((o as THREE.Mesh).isMesh && !(o as THREE.InstancedMesh).isInstancedMesh) caveMeshes.push(o); });
      const terrain: THREE.Object3D[] = [];
      this.scene.traverse((o) => { const m = o as THREE.Mesh; if (m.isMesh && m.geometry?.attributes?.biomeWA && m.visible) terrain.push(m); });
      const ray = new THREE.Raycaster();
      ray.far = 200;
      const shift = this.scene.position; // the FP64 rebase: rays live in render space
      let samples = 0, holes = 0, onCave = 0, onTerrain = 0, mouthOnCave = 0, mouthSamples = 0;
      const holeList: number[][] = [];
      for (let u = -CAVE_PORTAL.a * 1.2 + CAVE_PORTAL.u0; u <= CAVE_PORTAL.a * 1.2 + CAVE_PORTAL.u0; u += 1) {
        for (let v = -CAVE_PORTAL.b * 1.2; v <= CAVE_PORTAL.b * 1.2; v += 1) {
          const x = s.dx * u - s.dz * v, z = s.dz * u + s.dx * v;
          if (portalDome(s, x, z) <= 0) continue;
          samples++;
          const top = terrainHeight(this.seed, s.x + x, s.z + z) + 40;
          ray.set(new THREE.Vector3(s.x + x + shift.x, top + shift.y, s.z + z + shift.z), new THREE.Vector3(0, -1, 0));
          const hc = ray.intersectObjects(caveMeshes, true)[0];
          const ht = ray.intersectObjects(terrain, false)[0];
          const inMouth = u >= -2 && u <= 13 && Math.abs(v) <= 2;
          if (inMouth) mouthSamples++;
          if (!hc && !ht) { holes++; if (holeList.length < 12) holeList.push([Math.round(u), Math.round(v)]); continue; }
          if (hc && (!ht || hc.distance <= ht.distance)) { onCave++; if (inMouth) mouthOnCave++; } else onTerrain++;
        }
      }
      // The arch: from the porch along the heading at doorstep + 1.2 m.
      const p0 = b.caps.find((c) => !c.chamber);
      const ay = (p0 ? p0.ay : 1.45) + shift.y;
      ray.set(new THREE.Vector3(s.x - s.dx * 9 + shift.x, s.h + ay, s.z - s.dz * 9 + shift.z), new THREE.Vector3(s.dx, 0, s.dz).normalize());
      ray.far = 40;
      const arch = ray.intersectObjects(caveMeshes, true)[0];
      return { samples, holes, holeList, onCave, onTerrain, mouthSamples, mouthOnCave, terrainMeshes: terrain.length, archFreeM: arch ? Math.round(arch.distance * 10) / 10 : null, triangles: this.stats.lastTriangles };
    };
    (globalThis as unknown as { __caves?: () => unknown }).__caves = () => ({
      seed: this.seed, caveId: atlas.caveId, built: [...this.built.keys()], pending: [...this.pending], stats: this.stats,
      // world-space walk points of every built interior (probes walk them)
      paths: Object.fromEntries([...this.built.values()].map((b) => [b.site.id, b.chambers.length ? capsPath(b) : []])),
      near: this.seed ? caveSites(this.seed).map((c) => ({ id: c.id, d: Math.hypot(c.x - atlas.localX, c.z - atlas.localZ), x: c.x, z: c.z, h: c.h, dx: c.dx, dz: c.dz })).sort((a, b) => a.d - b.d).slice(0, 5) : [],
    });
  }

  /** The id of the built cavity holding world point (x, y, z) — capsule centre — or null. */
  private cavityAt(x: number, y: number, z: number): string | null {
    for (const b of this.built.values()) {
      const lx = x - b.site.x, lz = z - b.site.z, ly = y - b.site.h;
      if (lx * lx + lz * lz > 200 * 200) continue;
      if (caveDensity({ caps: b.caps } as CavePlan, lx, ly, lz) < 0.6) return `cave:${b.site.id}`;
    }
    return null;
  }

  static optedOut(): boolean {
    try { return localStorage.getItem('atlas:caves') === 'off'; } catch { return false; }
  }

  private commonsSeed(): string | null {
    const conn = atlas.conn;
    if (!conn) return null;
    for (const w of conn.db.world.iter()) if (w.kind === 'commons') return w.terrainSeed;
    return null;
  }

  private ensureWorker(): Worker | null {
    if (this.worker) return this.worker;
    if (typeof Worker === 'undefined') return null;
    try {
      this.worker = new Worker(new URL('../workers/cave_worker.ts', import.meta.url), { type: 'module' });
      this.worker.onmessage = (e: MessageEvent<CaveResult>) => this.onBuilt(e.data);
      if (this.seed) postRiverTable(this.worker, this.seed); // shared network (perf frontier item 12)
      this.worker.onerror = () => { this.worker = null; this.pending.clear(); };
    } catch { this.worker = null; }
    return this.worker;
  }

  private tick(): void {
    if (++this.frame % TICK_EVERY !== 0) return;
    if (atlas.currentWorldId === null || atlas.currentWorldId !== atlas.commonsWorldId || AtlasCaveModule.optedOut()) {
      if (this.built.size) for (const id of [...this.built.keys()]) this.drop(id);
      if (atlas.caveId !== null) atlas.caveId = null;
      return;
    }
    if (!this.seed) {
      const s = this.commonsSeed();
      if (!s) return;
      this.seed = s;
    }
    const px = atlas.localX, pz = atlas.localZ, py = atlas.localY;
    for (const c of caveSites(this.seed)) {
      const d = Math.hypot(c.x - px, c.z - pz);
      if (d < BUILD_R && !this.built.has(c.id) && !this.pending.has(c.id)) this.request(c);
      else if (d > DROP_R && this.built.has(c.id)) this.drop(c.id);
    }
    // The physics tick decides `atlas.caveId` at the body's own position
    // (setCaveTest); here only the light follows it.
    const inside = atlas.caveId ? this.built.get(Number(atlas.caveId.slice(5))) ?? null : null;
    void py;
    if (this.light) {
      if (inside) {
        let best = inside.chambers[0], bd = Infinity;
        for (const ch of inside.chambers) { const dd = Math.hypot(ch.x + inside.site.x - px, ch.z + inside.site.z - pz); if (dd < bd) { bd = dd; best = ch; } }
        if (best) { this.light.position.set(inside.site.x + best.x, inside.site.h + best.y + best.r * 0.4, inside.site.z + best.z); this.light.intensity = 26; this.light.visible = true; }
      } else {
        // Approaching a mouth: a low light just inside the arch so the
        // entrance reads as a tunnel with depth, not a black hole.
        let nearMouth: Built | null = null, nd = MOUTH_LIGHT_M;
        for (const b of this.built.values()) { const dd = Math.hypot(b.site.x - px, b.site.z - pz); if (dd < nd) { nd = dd; nearMouth = b; } }
        if (nearMouth) {
          const p = nearMouth.caps.find((c) => !c.chamber);
          const s = nearMouth.site;
          this.light.position.set(s.x + (p ? p.bx : s.dx * 2), s.h + (p ? p.by : 1.4) + 0.6, s.z + (p ? p.bz : s.dz * 2));
          this.light.intensity = 9; this.light.visible = true;
        } else if (this.light.visible) { this.light.visible = false; this.light.intensity = 0; }
      }
    }
  }

  private request(c: CaveSite): void {
    const w = this.ensureWorker();
    if (!w) return;
    this.pending.add(c.id);
    this.stats.requested++;
    const job: CaveJob = { seed: this.seed, siteId: c.id };
    w.postMessage(job);
  }

  private onBuilt(r: CaveResult): void {
    this.pending.delete(r.siteId);
    if (r.seed !== this.seed || this.built.has(r.siteId)) return;
    const site = caveSites(this.seed)[r.siteId];
    if (!site || r.indices.length === 0) return;
    if (Math.hypot(site.x - atlas.localX, site.z - atlas.localZ) > DROP_R) return;
    this.stats.builtMs = r.ms;
    this.stats.lastTriangles = r.indices.length / 3;
    const group = new THREE.Group();
    group.name = `cave:${site.id}`;
    group.position.set(site.x, site.h, site.z);
    group.matrixAutoUpdate = false; group.updateMatrix(); // static: the cave never moves
    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.BufferAttribute(r.positions, 3));
    geo.setAttribute('normal', new THREE.BufferAttribute(r.normals, 3));
    geo.setIndex(new THREE.BufferAttribute(r.indices, 1));
    const mesh = new THREE.Mesh(geo, this.rockMat ?? new THREE.MeshStandardMaterial({ color: 0x5a5148 }));
    mesh.receiveShadow = true;
    mesh.castShadow = true;
    mesh.frustumCulled = true;
    mesh.layers.enable(RAIN_SHELTER_LAYER); // the cave rock roofs the cut (render wave 1)
    geo.computeBoundingSphere();
    group.add(mesh);
    // Crystals along the walls (every CRYSTAL_EVERY path point, offset to a wall).
    const crystalGeo = new THREE.IcosahedronGeometry(0.35, 0);
    const n = Math.max(1, Math.floor(r.path.length / CRYSTAL_EVERY));
    const crystals = new THREE.InstancedMesh(crystalGeo, this.crystalMat ?? new THREE.MeshStandardMaterial({ color: 0x8fd8ff, emissive: 0x4fb8ff }), n);
    const m4 = new THREE.Matrix4();
    let ci = 0;
    for (let i = 1; i < r.path.length && ci < n; i += CRYSTAL_EVERY) {
      const p = r.path[i], q = r.path[i - 1];
      const dx = p.x - q.x, dz = p.z - q.z, l = Math.hypot(dx, dz) || 1;
      const side = i % 4 < 2 ? 1 : -1;
      const wx = p.x + (-dz / l) * (p.r * 0.8) * side, wz = p.z + (dx / l) * (p.r * 0.8) * side;
      const s = 0.7 + ((i * 7919) % 13) / 13 * 0.9;
      m4.compose(new THREE.Vector3(wx, p.y - p.r * 0.2 + ((i * 104729) % 7) / 7 * p.r * 0.6, wz), new THREE.Quaternion().setFromEuler(new THREE.Euler(i * 0.7, i * 1.3, i * 0.4)), new THREE.Vector3(s, s * 1.8, s));
      crystals.setMatrixAt(ci++, m4);
    }
    crystals.count = ci;
    pinStaticMotionVectors(crystals.material as THREE.Material); // static (three's frozen previous instance matrices)
    crystals.instanceMatrix.needsUpdate = true;
    group.add(crystals);
    // Fallen rocks at the foot of the portal's front face (the outcrop itself
    // is part of the cavity mesh — it roofs the terrain hole; the old boulder
    // collar that tried to hide that hole's edge is gone).
    const boulderGeo = new THREE.IcosahedronGeometry(1, 1);
    const boulders = new THREE.InstancedMesh(boulderGeo, this.rockMat ?? new THREE.MeshStandardMaterial({ color: 0x5a5148 }), 6);
    const px = -site.dz, pz = site.dx; // perpendicular
    let bi = 0;
    for (const sgn of [-1, 1]) {
      for (let k = 0; k < 3; k++) {
        const u = -4.6 + k * 1.5;
        const v = (MOUTH_R + 2.2 + k * 0.6) * sgn;
        const x = site.dx * u + px * v, z = site.dz * u + pz * v;
        const sc = 0.55 + ((k * 31 + (sgn + 1) * 17) % 5) / 5 * 0.6;
        m4.compose(new THREE.Vector3(x, sc * 0.35, z), new THREE.Quaternion().setFromEuler(new THREE.Euler(k * 0.9, sgn * k * 1.1, k * 0.5)), new THREE.Vector3(sc, sc * 0.75, sc * 1.2));
        boulders.setMatrixAt(bi++, m4);
      }
    }
    boulders.count = bi;
    pinStaticMotionVectors(boulders.material as THREE.Material);
    boulders.instanceMatrix.needsUpdate = true;
    boulders.castShadow = true;
    group.add(boulders);
    this.scene.add(group);
    // Collider: the cavity trimesh (double-sided in Rapier), at the site origin.
    const colliderId = `cave:${site.id}`;
    this.physics.addStaticCollider(colliderId, { shape: 'trimesh_static', positions: Array.from(r.positions), indices: Array.from(r.indices) }, { x: site.x, y: site.h, z: site.z, yaw: 0, scale: 1 });
    this.built.set(site.id, { group, colliderId, caps: capsFromPath(r.path, r.chambers), chambers: r.chambers, site });
  }

  private drop(id: number): void {
    const b = this.built.get(id);
    if (!b) return;
    this.physics.removeStaticCollider(b.colliderId);
    this.scene.remove(b.group);
    b.group.traverse((o) => {
      const m = o as THREE.Mesh;
      if (!m.isMesh) return;
      disposeLater(m.geometry);
      if ((m as THREE.InstancedMesh).isInstancedMesh) disposeLater(m as THREE.InstancedMesh);
    });
    this.built.delete(id);
    if (atlas.caveId === `cave:${id}`) atlas.caveId = null;
  }

  dispose(): void {
    for (const id of [...this.built.keys()]) this.drop(id);
    this.worker?.terminate();
    this.worker = null;
    this.physics.setCaveTest(null);
    if (this.light) { this.scene.remove(this.light); this.light = null; }
  }
}

/** World-space walk points (capsule ends) of a built interior. */
function capsPath(b: Built): Array<{ x: number; y: number; z: number }> {
  const out: Array<{ x: number; y: number; z: number }> = [];
  for (const c of b.caps) if (!c.chamber) out.push({ x: b.site.x + c.bx, y: b.site.h + c.by, z: b.site.z + c.bz });
  return out;
}

/** Rebuild the inside test's capsules from the path the worker sends back. */
function capsFromPath(path: Array<{ x: number; y: number; z: number; r: number }>, chambers: Array<{ x: number; y: number; z: number; r: number }>): CaveCapsule[] {
  const caps: CaveCapsule[] = [];
  for (let i = 1; i < path.length; i++) {
    const a = path[i - 1], b = path[i];
    caps.push({ ax: a.x, ay: a.y, az: a.z, bx: b.x, by: b.y, bz: b.z, r: Math.min(a.r, b.r, 3.4) });
  }
  for (const c of chambers) caps.push({ ax: c.x, ay: c.y, az: c.z, bx: c.x, by: c.y + 0.01, bz: c.z, r: c.r, chamber: true });
  return caps;
}
